import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Play, MessageSquare, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react'
import { api } from '../../api/client'
import { formatDistanceToNow } from 'date-fns'
import { pt } from 'date-fns/locale'

// ── Types ──────────────────────────────────────────────

type Tone = 'factual' | 'direct' | 'warm' | 'coaching'
type Verbosity = 'concise' | 'normal' | 'detailed'

interface AgentConfig {
  tone?: Tone | null
  verbosity?: Verbosity | null
  preferences?: string | null
}

interface Agent {
  id: string
  slug: string
  name: string
  description: string | null
  schedule: string | null
  is_active: boolean
  connectors_required: string[]
  config: AgentConfig | null
  last_run_at: string | null
  last_run_status: string | null
  created_at: string
}

interface AgentConversation {
  id: string
  title: string | null
  created_at: string
}

// ── Constants ──────────────────────────────────────────

const TONE_OPTIONS: { value: Tone; label: string; description: string }[] = [
  { value: 'factual', label: 'Factual', description: 'Directo aos dados e às acções' },
  { value: 'direct', label: 'Directo', description: 'Muito curto, verbos primeiro' },
  { value: 'warm', label: 'Caloroso', description: 'Reconhece o esforço sem ser piegas' },
  { value: 'coaching', label: 'Coach', description: 'Honesto, nota o que corre mal e bem' },
]

const VERBOSITY_OPTIONS: { value: Verbosity; label: string; description: string }[] = [
  { value: 'concise', label: 'Conciso', description: 'Só o essencial, listas curtas' },
  { value: 'normal', label: 'Normal', description: 'Equilibra concisão e contexto' },
  { value: 'detailed', label: 'Detalhado', description: 'Mais contexto e explicação' },
]

const FREQ_OPTIONS = [
  { label: 'Todos os dias',      days: '* * *' },
  { label: 'Dias de semana',     days: '* * 1-5' },
  { label: 'Fins de semana',     days: '* * 6,0' },
  { label: 'Só segundas-feiras', days: '* * 1' },
]

// ── Helpers ────────────────────────────────────────────

function statusDot(status: string | null) {
  if (status === 'success') return { background: 'var(--accent)' }
  if (status === 'failure') return { background: '#ef4444' }
  if (status === 'running') return { background: 'var(--ink-muted)', animation: 'pulse 2s infinite' }
  return { background: 'var(--border)' }
}

// Parse cron "MM HH * * <dow>" → { time: "HH:MM", days: "* * <dow>" }
function parseCron(cron: string | null): { time: string; days: string } {
  if (!cron) return { time: '09:00', days: '* * *' }
  const parts = cron.split(' ')
  if (parts.length < 5) return { time: '09:00', days: '* * *' }
  const [min, hour, , , dow] = parts
  const time = `${hour.padStart(2, '0')}:${min.padStart(2, '0')}`
  const days = `* * ${dow}`
  return { time, days }
}

function buildCron(time: string, days: string): string {
  const [h, m] = time.split(':')
  const dow = days.split(' ')[2]
  return `${parseInt(m)} ${parseInt(h)} * * ${dow}`
}

// Human-readable schedule summary for the collapsed card row.
// parseCron already handles the common formats we produce ourselves;
// for anything exotic (e.g. Postman's "*/30 9-20 * * *"), we fall back
// to showing the raw cron so the user at least sees *something*.
function humanSchedule(cron: string | null): string {
  if (!cron) return 'Sem agendamento'
  const parts = cron.split(' ')
  if (parts.length < 5) return cron
  const [min, hour, dom, month, dow] = parts

  // Postman-style: every N minutes within an hour range
  if (min.startsWith('*/') && hour.includes('-')) {
    const every = min.slice(2)
    const [from, to] = hour.split('-')
    return `A cada ${every} min, das ${from}h às ${to}h`
  }

  // Standard daily/weekly schedule
  if (dom === '*' && month === '*' && /^\d+$/.test(min) && /^\d+$/.test(hour)) {
    const time = `${hour.padStart(2, '0')}:${min.padStart(2, '0')}`
    const dayLabel =
      dow === '*' ? 'Todos os dias' :
      dow === '1-5' ? 'Dias de semana' :
      dow === '6,0' ? 'Fins de semana' :
      dow === '0' ? 'Domingos' :
      dow === '1' ? 'Segundas' :
      dow === '2' ? 'Terças' :
      dow === '3' ? 'Quartas' :
      dow === '4' ? 'Quintas' :
      dow === '5' ? 'Sextas' :
      dow === '6' ? 'Sábados' :
      `cron: ${cron}`
    return `${dayLabel} às ${time}`
  }

  return cron
}

// ── Card ───────────────────────────────────────────────

function AgentCard({ agent, onUpdated }: { agent: Agent; onUpdated: () => void }) {
  const navigate = useNavigate()
  const parsed = parseCron(agent.schedule)
  const [open, setOpen] = useState(false)
  const [running, setRunning] = useState(false)

  // Schedule state (saved on-blur, no explicit save button)
  const [editTime, setEditTime] = useState(parsed.time)
  const [editDays, setEditDays] = useState(parsed.days)

  // Personalisation state (tom/verbosidade/notas — explicit save)
  const [tone, setTone] = useState<Tone | null>(agent.config?.tone ?? null)
  const [verbosity, setVerbosity] = useState<Verbosity | null>(agent.config?.verbosity ?? null)
  const [preferences, setPreferences] = useState<string>(agent.config?.preferences ?? '')
  const [savingConfig, setSavingConfig] = useState(false)
  const [savedFlash, setSavedFlash] = useState(false)

  // Recent conversations (loaded lazily when card expands)
  const [conversations, setConversations] = useState<AgentConversation[]>([])
  useEffect(() => {
    if (!open) return
    api.get<AgentConversation[]>(`/agents/${agent.id}/conversations`)
      .then(setConversations).catch(() => {})
  }, [agent.id, open])

  async function run() {
    setRunning(true)
    try {
      const data = await api.post<{ run_id: string; conversation_id: string }>(`/agents/${agent.id}/run`)
      navigate(`/chat/${data.conversation_id}`)
    } catch (err) {
      alert(`Erro: ${err instanceof Error ? err.message : 'falha'}`)
      setRunning(false)
    }
  }

  async function saveSchedule(time: string, days: string) {
    if (!agent.schedule) return
    try {
      await api.put(`/agents/${agent.id}`, { schedule: buildCron(time, days) })
      onUpdated()
    } catch { /* silent — next refetch corrects drift */ }
  }

  async function saveConfig() {
    setSavingConfig(true)
    try {
      const trimmed = preferences.trim()
      const config: AgentConfig = {
        tone: tone ?? null,
        verbosity: verbosity ?? null,
        preferences: trimmed ? trimmed : null,
      }
      await api.put(`/agents/${agent.id}`, { config })
      setSavedFlash(true)
      setTimeout(() => setSavedFlash(false), 1800)
      onUpdated()
    } catch (err) {
      alert(`Erro ao guardar: ${err instanceof Error ? err.message : 'falha'}`)
    }
    setSavingConfig(false)
  }

  // Dirty check so the Save button only lights up when the form has
  // deviations from the last persisted config.
  const original = {
    tone: agent.config?.tone ?? null,
    verbosity: agent.config?.verbosity ?? null,
    preferences: (agent.config?.preferences ?? '').trim(),
  }
  const isDirty =
    original.tone !== tone ||
    original.verbosity !== verbosity ||
    original.preferences !== preferences.trim()

  return (
    <div className="bg-white rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
      {/* Collapsed row — always visible */}
      <div className="px-4 py-4 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="mt-1.5 w-2 h-2 rounded-full shrink-0" style={statusDot(agent.last_run_status)} />
          <div className="min-w-0">
            <h3 className="text-sm font-medium" style={{ color: 'var(--ink)' }}>{agent.name}</h3>
            {agent.schedule && (
              <p className="text-xs mt-0.5" style={{ color: 'var(--ink-muted)' }}>
                {humanSchedule(agent.schedule)}
              </p>
            )}
            {agent.description && (
              <p className="text-xs mt-1 leading-relaxed" style={{ color: 'var(--ink-muted)' }}>{agent.description}</p>
            )}
            {agent.last_run_at && (
              <p className="text-xs mt-1" style={{ color: 'var(--ink-muted)' }}>
                Última execução {formatDistanceToNow(new Date(agent.last_run_at), { locale: pt, addSuffix: true })}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={run}
            disabled={running}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border transition-colors disabled:opacity-40"
            style={{ borderColor: 'var(--border)', color: 'var(--ink-light)' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'var(--cream-dark)')}
            onMouseLeave={e => (e.currentTarget.style.background = '')}
          >
            {running ? <RotateCcw size={12} className="animate-spin" /> : <Play size={12} />}
            {running ? 'A iniciar...' : 'Correr'}
          </button>
          <button
            onClick={() => setOpen(o => !o)}
            className="p-1.5 transition-colors"
            style={{ color: 'var(--ink-muted)' }}
            title={open ? 'Fechar' : 'Personalizar'}
          >
            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {/* Expanded — schedule + personalisation + history */}
      {open && (
        <div className="border-t" style={{ borderColor: 'var(--border)' }}>

          {/* Schedule */}
          {agent.schedule && (
            <div className="px-4 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
              <label className="block text-xs font-medium mb-2" style={{ color: 'var(--ink-muted)' }}>
                Quando corre
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={editDays}
                  onChange={e => { setEditDays(e.target.value); saveSchedule(editTime, e.target.value) }}
                  className="text-xs border rounded-md px-2 py-1.5 focus:outline-none"
                  style={{ borderColor: 'var(--border)', color: 'var(--ink-light)', background: 'var(--cream)' }}
                >
                  {FREQ_OPTIONS.map(o => (
                    <option key={o.days} value={o.days}>{o.label}</option>
                  ))}
                </select>
                <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>às</span>
                <input
                  type="time"
                  value={editTime}
                  onChange={e => setEditTime(e.target.value)}
                  onBlur={e => saveSchedule(e.target.value, editDays)}
                  className="text-xs border rounded-md px-2 py-1.5 focus:outline-none w-24"
                  style={{ borderColor: 'var(--border)', color: 'var(--ink-light)', background: 'var(--cream)' }}
                />
              </div>
            </div>
          )}

          {/* Personalisation */}
          <div className="px-4 py-5 space-y-5 border-b" style={{ borderColor: 'var(--border)' }}>

            {/* Tom */}
            <div>
              <label className="block text-xs font-medium mb-2" style={{ color: 'var(--ink-muted)' }}>
                Tom
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <button
                  type="button"
                  onClick={() => setTone(null)}
                  className="text-left border rounded-md px-3 py-2 transition-colors"
                  style={{
                    borderColor: tone === null ? 'var(--accent)' : 'var(--border)',
                    background: tone === null ? 'rgba(45,90,61,0.06)' : 'transparent',
                  }}
                >
                  <div className="text-xs font-medium" style={{ color: 'var(--ink)' }}>Padrão</div>
                  <div className="text-[10px] mt-0.5" style={{ color: 'var(--ink-muted)' }}>Sem preferência</div>
                </button>
                {TONE_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setTone(opt.value)}
                    className="text-left border rounded-md px-3 py-2 transition-colors"
                    style={{
                      borderColor: tone === opt.value ? 'var(--accent)' : 'var(--border)',
                      background: tone === opt.value ? 'rgba(45,90,61,0.06)' : 'transparent',
                    }}
                  >
                    <div className="text-xs font-medium" style={{ color: 'var(--ink)' }}>{opt.label}</div>
                    <div className="text-[10px] mt-0.5" style={{ color: 'var(--ink-muted)' }}>{opt.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Verbosidade */}
            <div>
              <label className="block text-xs font-medium mb-2" style={{ color: 'var(--ink-muted)' }}>
                Verbosidade
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <button
                  type="button"
                  onClick={() => setVerbosity(null)}
                  className="text-left border rounded-md px-3 py-2 transition-colors"
                  style={{
                    borderColor: verbosity === null ? 'var(--accent)' : 'var(--border)',
                    background: verbosity === null ? 'rgba(45,90,61,0.06)' : 'transparent',
                  }}
                >
                  <div className="text-xs font-medium" style={{ color: 'var(--ink)' }}>Padrão</div>
                  <div className="text-[10px] mt-0.5" style={{ color: 'var(--ink-muted)' }}>Sem preferência</div>
                </button>
                {VERBOSITY_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setVerbosity(opt.value)}
                    className="text-left border rounded-md px-3 py-2 transition-colors"
                    style={{
                      borderColor: verbosity === opt.value ? 'var(--accent)' : 'var(--border)',
                      background: verbosity === opt.value ? 'rgba(45,90,61,0.06)' : 'transparent',
                    }}
                  >
                    <div className="text-xs font-medium" style={{ color: 'var(--ink)' }}>{opt.label}</div>
                    <div className="text-[10px] mt-0.5" style={{ color: 'var(--ink-muted)' }}>{opt.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Notas livres — per rotina */}
            <div>
              <label className="block text-xs font-medium mb-2" style={{ color: 'var(--ink-muted)' }}>
                Notas para esta rotina
              </label>
              <textarea
                value={preferences}
                onChange={e => setPreferences(e.target.value.slice(0, 1000))}
                rows={3}
                placeholder="Ex: 'exclui a venture X dos briefings', 'dá-me sempre contexto de margem'..."
                className="w-full border rounded-md p-3 text-sm focus:outline-none resize-y"
                style={{ borderColor: 'var(--border)', background: 'var(--cream)', color: 'var(--ink)', lineHeight: '1.6' }}
              />
              <div className="flex justify-between items-center mt-1">
                <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                  O seGuru vai respeitar estas preferências sem as mencionar explicitamente.
                </p>
                <span className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                  {preferences.length}/1000
                </span>
              </div>
            </div>

            {/* Save */}
            <div className="flex justify-end items-center gap-2">
              {savedFlash && (
                <span className="text-xs" style={{ color: 'var(--accent)' }}>Guardado</span>
              )}
              <button
                onClick={saveConfig}
                disabled={!isDirty || savingConfig}
                className="px-4 py-1.5 text-xs rounded-md transition-colors disabled:opacity-40"
                style={{
                  background: isDirty ? 'var(--ink)' : 'var(--border)',
                  color: isDirty ? 'var(--cream)' : 'var(--ink-muted)',
                }}
              >
                {savingConfig ? 'A guardar...' : 'Guardar personalização'}
              </button>
            </div>
          </div>

          {/* Conversas recentes — loaded on expand */}
          {conversations.length > 0 && (
            <div className="px-4 py-3">
              <p className="text-xs font-medium mb-2" style={{ color: 'var(--ink-muted)' }}>
                Últimas conversas
              </p>
              <div className="space-y-0.5">
                {conversations.map(conv => (
                  <button
                    key={conv.id}
                    onClick={() => navigate(`/chat/${conv.id}`)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded transition-colors text-left"
                    style={{ color: 'var(--ink-muted)' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--cream-dark)'; e.currentTarget.style.color = 'var(--ink-light)' }}
                    onMouseLeave={e => { e.currentTarget.style.background = ''; e.currentTarget.style.color = 'var(--ink-muted)' }}
                  >
                    <MessageSquare size={11} className="shrink-0" style={{ color: 'var(--ink-muted)' }} strokeWidth={1.5} />
                    <span className="truncate flex-1">{conv.title || 'Conversa'}</span>
                    <span className="shrink-0" style={{ color: 'var(--ink-muted)' }}>
                      {formatDistanceToNow(new Date(conv.created_at), { locale: pt, addSuffix: true })}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Page ───────────────────────────────────────────────

export function AgentsPage() {
  const [agents, setAgents] = useState<Agent[]>([])
  const [loading, setLoading] = useState(true)

  function loadAgents() {
    api.get<Agent[]>('/agents').then(setAgents).catch(() => {}).finally(() => setLoading(false))
  }

  useEffect(() => { loadAgents() }, [])

  if (loading) return (
    <div className="flex items-center justify-center h-full text-sm" style={{ color: 'var(--ink-muted)', fontFamily: 'var(--sans)' }}>
      A carregar rotinas...
    </div>
  )

  return (
    <div className="flex-1 overflow-y-auto" style={{ background: 'var(--cream)', fontFamily: 'var(--sans)' }}>
      <div className="max-w-2xl mx-auto p-4 md:p-8">
        <h1 className="text-lg font-semibold mb-2" style={{ color: 'var(--ink)', fontFamily: 'var(--serif)' }}>Rotinas</h1>
        <p className="text-sm mb-6" style={{ color: 'var(--ink-muted)' }}>
          O que o seGuru faz por ti, sem teres de pedir. Clica no {'>'} para personalizar cada uma.
        </p>
        <div className="space-y-3">
          {agents.map(agent => <AgentCard key={agent.id} agent={agent} onUpdated={loadAgents} />)}
        </div>
      </div>
    </div>
  )
}
